import { useState } from 'react';
import { API_BASE_URL } from 'sharedKernel/constants';
import { cookies } from 'sharedKernel/cookies/behavior';

interface VideoUploadState {
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  videoId: string | null;
}

export const useLessonVideoUpload = () => {
  const [state, setState] = useState<VideoUploadState>({
    isUploading: false,
    uploadProgress: 0,
    error: null,
    videoId: null,
  });

  const getToken = () => {
    // Read through the cookie wrapper: names are scoped per tenant+env, so a raw
    // `document.cookie` scan for `_cred=` finds nothing now — and used to find the WRONG
    // tenant's cookie, which is the defect the scoping exists to fix.
    return localStorage.getItem('jwt_token') ||
           sessionStorage.getItem('jwt') ||
           cookies.get('_cred')?.data?.token;
  };

  const getVideoDurationMinutes = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const url = URL.createObjectURL(file);
      video.src = url;
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const seconds = video.duration || 0;
        const minutes = Math.max(1, Math.ceil(seconds / 60));
        resolve(minutes);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(1); 
      };
    });
  };

  const uploadVideoToLesson = async (lessonId: string, videoFile: File): Promise<string> => {
    const formData = new FormData();
    formData.append('video', videoFile);
    const minutes = await getVideoDurationMinutes(videoFile);
    formData.append('minute', String(minutes));

    try {
      setState(prev => ({ 
        ...prev, 
        isUploading: true, 
        uploadProgress: 0, 
        error: null 
      }));

      const token = getToken();
      if (!token) {
        throw new Error('Token de autorização não encontrado');
      }

      const response = await fetch(`${API_BASE_URL}v1/lesson/upload-video/${lessonId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro no upload:', errorText);
        throw new Error(`Erro no upload: ${response.status} ${response.statusText}`);
      }

      let returnedId: string | null = null;
      try {
        const result = await response.json();
        returnedId = result?.videoId || result?.id || null;
      } catch {}
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        videoId: returnedId 
      }));

      return returnedId || '';
    } catch (error: any) {
      console.error('❌ Erro no upload:', error);
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        error: error.message 
      }));
      throw error;
    }
  };

  const resetState = () => {
    setState({
      isUploading: false,
      uploadProgress: 0,
      error: null,
      videoId: null,
    });
  };

  return {
    ...state,
    uploadVideoToLesson,
    resetState,
  };
};
